import { ConfigureTaskDrawerContent } from './ConfigureTaskDrawer/ConfigureTaskDrawerContent';
import { Stack } from '@mui/material';
import { useMemo } from 'react';
import { EditWorkflowType, FlowResourceType, useEditorStore } from '@diana/core';
import { useFlowBuilder2PageContext } from '../../Context/useFlowBuilder2PageContext';
import { useFlowBuilder2PageStore } from '../../Store/useFlowBuilder2PageStore';
import { ConfigureComponentProps } from './ConfigureComponentDrawer/ConfigureComponentProps';
import { ConfigureDrawerContent } from './ConfigureDrawer.styles';
import { EditAssistantChat } from '../../Components/EditAssistant/EditAssistantChat';
import {
  BuilderConfigureEditorTabEnum,
  BuilderToolsDrawerTabValue,
  BuilderTypeEnum,
} from '../../Store/FlowBuilder2PageStore.types';
import { ConfigureDrawerContentLoader } from './ConfigureDrawerContentLoader';
import { useFindActiveTask } from '../../Support/useFindActiveTask';
import { LayoutComponentEditor } from './ConfigureComponentDrawer/LayoutComponentEditor/LayoutComponentEditor';

export const ConfigureEditorDrawerContent = () => {
  const { isLoading } = useFlowBuilder2PageContext();
  const findActiveTask = useFindActiveTask();

  const hasActiveTask = useFlowBuilder2PageStore((s) => !!s.activeTaskId);
  const activeComponent = useFlowBuilder2PageStore((s) => s.activeComponent);
  const rightDrawerActiveTab = useFlowBuilder2PageStore((s) => s.rightDrawerActiveTab);
  const isExplorerOpen = useFlowBuilder2PageStore(
    (s) => s.leftDrawerActiveTab === BuilderToolsDrawerTabValue.Explorer,
  );
  const isAdvancedEditorOpen = useFlowBuilder2PageStore(
    (s) => s.builderType === BuilderTypeEnum.AdvancedEditor,
  );

  const flowResourceType = useEditorStore((s) => s.flow?.properties?.flowResourceType);
  const firstLayoutId = useEditorStore((s) => s.flow?.steps?.[0]?.layoutId ?? '');

  const hasActiveComponent = !!activeComponent;
  const isEntity = flowResourceType === FlowResourceType.Entity;

  const chatType = useMemo(() => {
    const flowType = isEntity ? EditWorkflowType.Layout : EditWorkflowType.Flow;
    if (!isExplorerOpen) return flowType;

    switch (true) {
      case hasActiveComponent:
        return EditWorkflowType.Component;
      case hasActiveTask:
        return EditWorkflowType.Layout;
      default:
        return flowType;
    }
  }, [isExplorerOpen, hasActiveComponent, hasActiveTask, isEntity]);

  const currentLayoutId = useMemo(() => {
    if (!isExplorerOpen) return undefined;

    switch (true) {
      case hasActiveComponent:
        return activeComponent?.layoutId;
      case hasActiveTask:
        return findActiveTask()?.layoutId;
      default:
        return isEntity ? firstLayoutId : undefined;
    }
  }, [
    findActiveTask,
    hasActiveComponent,
    hasActiveTask,
    isEntity,
    firstLayoutId,
    activeComponent?.layoutId,
    isExplorerOpen,
  ]);

  const currentComponentId = useMemo(() => {
    if (!isExplorerOpen) return undefined;
    return activeComponent?.componentId;
  }, [isExplorerOpen, activeComponent?.componentId]);

  const showChat =
    isAdvancedEditorOpen ||
    rightDrawerActiveTab === BuilderConfigureEditorTabEnum.AskDiana ||
    (!hasActiveComponent && !hasActiveTask);

  const configureDrawerContent = useMemo(() => {
    switch (true) {
      case hasActiveComponent:
        return (
          <ConfigureComponentProps>
            {(props) => <LayoutComponentEditor {...props} />}
          </ConfigureComponentProps>
        );
      case hasActiveTask:
        return <ConfigureTaskDrawerContent />;
      default:
        return null;
    }
  }, [hasActiveComponent, hasActiveTask]);

  return isLoading ? (
    <ConfigureDrawerContent>
      <ConfigureDrawerContentLoader />
    </ConfigureDrawerContent>
  ) : showChat ? (
    <ConfigureDrawerContent>
      <Stack justifyContent="flex-end" height="100%">
        <EditAssistantChat
          type={chatType}
          resourceType={flowResourceType ?? FlowResourceType.Work}
          layoutId={currentLayoutId}
          componentId={currentComponentId}
          allowFileUpload
        />
      </Stack>
    </ConfigureDrawerContent>
  ) : (
    configureDrawerContent
  );
};
